import { Injectable } from '@angular/core';
import { StateService, UIRouterGlobals } from '@uirouter/core';
import {
  ProcessErrorCodeAdapterService,
  ProcessErrorCodeFn,
} from 'src/app/components/scrolling-list/services/process-error-code-adapter.service';
import { ApiListResponse } from 'src/app/api/api';
import { type Display } from 'src/app/api/services/display';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { ScheduleApiService } from 'src/app/api/services/schedule-api.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { ENTITY_TYPES } from 'src/app/auth/types/entity-type';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { ScheduleService } from 'src/app/schedules/services/schedule.service';
import { HasFolderStructure } from 'src/app/shared/services/folder.service';
import { DisplayActivationService } from './display-activation.service';
import { PlayerLicenseService } from './player-license.service';

@Injectable({
  providedIn: 'root',
})
export class DisplayService implements HasFolderStructure<Display> {
  static readonly playerNamesWithHiddenProductNames = ['Android Player'];

  loadingDisplay: boolean;
  savingDisplay: boolean;
  errorMessage: string;
  apiError: string;
  display: Display;
  scheduleTypeName: string;
  companyId: string;

  private processErrorCode: ProcessErrorCodeFn;

  constructor(
    private trackerService: TrackerService,
    private featuresService: FeaturesService,
    private playerLicenseService: PlayerLicenseService,
    private displayApiService: DisplayApiService,
    private scheduleApiService: ScheduleApiService,
    private stateService: StateService,
    private uiRouterGlobals: UIRouterGlobals,
    private scheduleService: ScheduleService,
    processErrorCodeAdapter: ProcessErrorCodeAdapterService,
    private userStateService: UserStateService,
    private permissionService: PermissionService,
    private currentPlanService: CurrentPlanService,
    private modalService: ModalService,
    // Initialize service to handle events
    // Should use APP_INITIALIZER but it requires AJS dependencies so it fails
    displayActivationService: DisplayActivationService,
  ) {
    this.processErrorCode = processErrorCodeAdapter.createCallable();

    this.init();
  }

  private _clearMessages() {
    this.loadingDisplay = false;
    this.savingDisplay = false;

    this.apiError = '';
  }

  init() {
    this.display = {
      width: 1920,
      height: 1080,
      status: 1,
      restartEnabled: true,
      restartTime: '02:00',
      autostartOnBoot: true,
      monitoringEnabled: false,
      useCompanyAddress: true,
      playerProAssigned: false,
      playerProAuthorized: false,
      isDisplayControlEnabled: false,
      settings: {
        volumeControlSupported: false,
        volumePercentage: 75,
      },
    };

    this._clearMessages();
  }

  newDisplay() {
    this.trackerService.displayEvent('Add Display');

    this.init();

    if (this.featuresService.isFeatureAvailable('monitoring')) {
      this.display.monitoringEnabled = true;
    }

    if (this.playerLicenseService.isProAvailable(this.display)) {
      this.display.playerProAssigned = true;
      this.display.playerProAuthorized = true;
    }
  }

  getDisplay(displayId): Promise<void> {
    this._clearMessages();

    //show loading spinner
    this.loadingDisplay = true;

    return this.displayApiService
      .get(displayId)
      .then((result) => {
        this.display = this.normalizeApiResponse(result.item);
        this.display.originalPlayerProAuthorized =
          this.display.playerProAuthorized;
        this.display.originalInteractiveAuthorized =
          this.display.interactiveAuthorized;

        this.display.overrideSchedules = [];
        if (this.hasOverrideSchedule(this.display)) {
          for (let i = 0; i < this.display.overrideScheduleIds.length; i++) {
            this.display.overrideSchedules.push({
              id: this.display.overrideScheduleIds[i],
              name: this.display.overrideScheduleNames[i],
              scheduleTypeName: '',
            });

            this.scheduleApiService
              .get(this.display.overrideScheduleIds[i])
              .then((result) => {
                if (result.item) {
                  this.display.overrideSchedules[i].scheduleTypeName =
                    result.item.scheduleTypeName;
                  this.display.overrideSchedules[i].companyId =
                    result.item.companyId;
                }
              });
          }
        }

        const loadedDisplayId = this.display.id;

        if (!loadedDisplayId) {
          return;
        }

        return this.permissionService
          .loadShareAccess(ENTITY_TYPES.DISPLAY, loadedDisplayId)
          .then(() => undefined)
          .catch(() => undefined);
      })
      .then(null, (e) => {
        this._showErrorMessage('get', e);

        return Promise.reject();
      })
      .finally(() => {
        this.loadingDisplay = false;
      });
  }

  addDisplay(selectedSchedule) {
    this._clearMessages();

    //show loading spinner
    this.loadingDisplay = true;
    this.savingDisplay = true;

    return this.displayApiService
      .add(this.display)
      .then(
        (resp) => {
          if (resp && resp.item && resp.item.id) {
            if (this.display.playerProAuthorized) {
              this.playerLicenseService.toggleDisplayLicenseLocal(true);
            }

            if (this.display.activationKey) {
              this.trackerService.displayEvent(
                'Display Activated',
                resp.item.id,
                resp.item.name,
              );
            } else {
              this.trackerService.displayEvent(
                'Display Created',
                resp.item.id,
                resp.item.name,
              );
            }

            return this.scheduleService
              .addToDistribution(resp.item, selectedSchedule)
              .then(() => {
                if (
                  this.uiRouterGlobals.current.name === 'apps.displays.add' ||
                  this.uiRouterGlobals.current.name === 'apps.displays.activate'
                ) {
                  this.stateService.go('apps.displays.details', {
                    displayId: resp.item.id,
                    activated: !!this.display.activationKey,
                  });
                }

                return Promise.resolve();
              })
              .catch(() => {
                this.apiError = this.scheduleService.apiError;
              });
          } else {
            return Promise.reject();
          }
        },
        (e) => {
          this._showErrorMessage('add', e);
          return Promise.reject();
        },
      )
      .finally(() => {
        this.loadingDisplay = false;
        this.savingDisplay = false;
      });
  }

  updateDisplay(selectedSchedule) {
    this._clearMessages();

    //show loading spinner
    this.loadingDisplay = true;
    this.savingDisplay = true;

    return this._update(this.display.id, this.display)
      .then(() => {
        this._updateLicenseIfNeeded();
        return Promise.resolve();
      })
      .then(() => {
        return this.scheduleService
          .addToDistribution(this.display, selectedSchedule)
          .then(() => {
            return Promise.resolve();
          })
          .catch(() => {
            this.apiError = this.scheduleService.apiError;
          });
      })
      .catch((e) => {
        this._showErrorMessage('update', e);
        return Promise.reject();
      })
      .finally(() => {
        this.loadingDisplay = false;
        this.savingDisplay = false;
      });
  }

  private _update(displayId, fields) {
    return this.displayApiService.update(displayId, fields).then((result) => {
      this.trackerService.displayEvent(
        'Display Updated',
        displayId,
        fields && fields.name,
      );
      return result;
    });
  }

  private _updateLicenseIfNeeded() {
    if (
      this.display.playerProAuthorized !==
      this.display.originalPlayerProAuthorized
    ) {
      this.playerLicenseService.updateLicense({
        display: this.display,
        success: () => {
          this.display.originalPlayerProAuthorized =
            this.display.playerProAuthorized;
        },
        error: (error) => {
          this.apiError = this.processErrorCode(error);
          this._showErrorMessage('update', error);
        },
        licenseType: PlayerLicenseService.DISPLAY_LICENSE_TYPE,
      });
    }
    if (
      this.display.interactiveAuthorized !==
      this.display.originalInteractiveAuthorized
    ) {
      this.playerLicenseService.updateLicense({
        display: this.display,
        success: () => {
          this.display.originalInteractiveAuthorized =
            this.display.interactiveAuthorized;
        },
        error: (error) => {
          this.apiError = this.processErrorCode(error);
          this._showErrorMessage('update', error);
        },
        licenseType: PlayerLicenseService.INTERACTIVE_LICENSE_TYPE,
      });
    }
  }

  applyFields(display, fields) {
    return this._update(display.id, fields).then((result) => {
      Object.assign(display, fields);
      return result;
    });
  }

  deleteDisplayByObject(displayObject) {
    return this.displayApiService.delete(displayObject.id).then(() => {
      this.trackerService.displayEvent(
        'Display Deleted',
        displayObject.id,
        displayObject.name,
      );

      if (displayObject.playerProAssigned) {
        this.playerLicenseService.toggleDisplayLicenseLocal(false);
      }
    });
  }

  deleteDisplay() {
    this._clearMessages();

    //show loading spinner
    this.loadingDisplay = true;

    return this.deleteDisplayByObject(this.display)
      .then(() => {
        this.display = {};

        this.stateService.go('apps.displays.list');
      })
      .then(null, (e) => {
        this._showErrorMessage('delete', e);
      })
      .finally(() => {
        this.loadingDisplay = false;
      });
  }

  private _showErrorMessage(action, e) {
    this.errorMessage = 'Failed to ' + action + ' display.';
    this.apiError = this.processErrorCode('Display', action, e);

    console.error(this.apiError, e);
  }

  showLicenseRequired(display?: Display): boolean {
    display = display || this.display;

    return (
      display &&
      !display.playerProAuthorized &&
      !this.userStateService.isRiseAdmin()
    );
  }

  showProductName() {
    const display = this.display;

    return (
      display &&
      display.productName &&
      (!DisplayService.playerNamesWithHiddenProductNames.includes(
        display.playerName,
      ) ||
        this.userStateService.isRiseAdmin())
    );
  }

  hasSchedule(display: Display): boolean {
    return display && display.scheduleId && display.scheduleId !== 'DEMO';
  }

  hasOverrideSchedule(display: Display): boolean {
    return (
      display &&
      display.overrideScheduleIds &&
      display.overrideScheduleIds.length > 0
    );
  }

  toggleProAuthorized() {
    if (
      !this.playerLicenseService.isProAvailable(this.display) &&
      !this.display.originalPlayerProAuthorized
    ) {
      this.display.playerProAuthorized = false;
      this.currentPlanService.confirmAndPurchase();
    } else {
      this.playerLicenseService.updateDisplayLicenseLocal(this.display);
      this.playerLicenseService.toggleDisplayLicenseLocal(
        this.display.playerProAuthorized,
        1,
      );
    }
  }

  toggleInteractiveAuthorized() {
    if (
      !this.playerLicenseService.isInteractiveAvailable(this.display) &&
      !this.display.originalInteractiveAuthorized
    ) {
      this.display.interactiveAuthorized = false;
    } else {
      this.playerLicenseService.updateDisplayInteractiveLocal(this.display);
      this.playerLicenseService.toggleInteractiveLicenseLocal(
        this.display.interactiveAuthorized,
        1,
      );
    }
  }

  private normalizeApiResponse(display: Display) {
    display.settings = display.settings || {};

    // if it's already a boolean or absent, preserve as-is (falsey → false, truthy boolean → true)
    if (typeof display.settings.scaleContentToFitScreen === 'string') {
      display.settings.scaleContentToFitScreen =
        display.settings.scaleContentToFitScreen === 'true';
    }
    if (typeof display.settings.screenSharingCodeHidden === 'string') {
      display.settings.screenSharingCodeHidden =
        display.settings.screenSharingCodeHidden === 'true';
    }
    if (typeof display.settings.screenSharingPinRequired === 'string') {
      display.settings.screenSharingPinRequired =
        display.settings.screenSharingPinRequired === 'true';
    }
    if (
      typeof display.settings.screenSharingAuthenticationRequired === 'string'
    ) {
      display.settings.screenSharingAuthenticationRequired =
        display.settings.screenSharingAuthenticationRequired === 'true';
    }
    if (typeof display.settings.volumeControlSupported === 'string') {
      display.settings.volumeControlSupported =
        display.settings.volumeControlSupported === 'true';
    }
    if (typeof display.settings.volumePercentage === 'string') {
      display.settings.volumePercentage = parseInt(
        display.settings.volumePercentage,
        10,
      );
    }

    return display;
  }

  confirmAssignLicense(selectedSchedule) {
    if (
      this.scheduleService.requiresLicense(selectedSchedule) &&
      !this.display.playerProAuthorized
    ) {
      this.modalService
        .confirm(
          'Assign license?',
          "You've selected a schedule that contains presentations. In order to show this schedule on this display, you need to license it. Assign license now?",
          'Yes',
          'No',
        )
        .then(() => {
          // Toggle license as if they clicked the checkbox
          this.display.playerProAuthorized = true;

          this.toggleProAuthorized();
        });
    }
  }

  isGiadaDisplay() {
    return this.display && this.display.manufacturer === 'Giada';
  }

  isOrientationPortrait() {
    return this.display && [90, 270].includes(Number(this.display.orientation));
  }

  hasPortraitResolution() {
    return this.display && this.display.width < this.display.height;
  }

  listByFolderHierarchy(
    folderId: string,
    cursor?: string | null,
  ): Promise<ApiListResponse<Display>> {
    return this.displayApiService.list(
      {
        filter: `folderHierarchy:${folderId}`,
      },
      cursor,
    );
  }

  revertLicenseChangesIfNeeded() {
    if (
      this.display.playerProAuthorized !==
      this.display.originalPlayerProAuthorized
    ) {
      this.playerLicenseService.toggleDisplayLicenseLocal(
        this.display.originalPlayerProAuthorized,
        1,
      );
    }
    if (
      this.display.interactiveAuthorized !==
      this.display.originalInteractiveAuthorized
    ) {
      this.playerLicenseService.toggleInteractiveLicenseLocal(
        this.display.originalInteractiveAuthorized,
        1,
      );
    }
  }
}
